import { cn } from "@/utils/theme";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
} from "@elaniintc/goes-design-system";
import { useState, type ReactNode } from "react";
import type { ActionItemType } from "../types";

export interface ActionItem {
  id: ActionItemType;
  name: string;
  description: string;
  value: string;
  icon: ReactNode;
}

// eslint-disable-next-line react-refresh/only-export-components
export const actionList: ActionItem[] = [
  {
    id: "read",
    value: "Leer",
    name: "Leer Expediente",
    description: "Trae datos del expediente para utilizarlos en el flujo.",
    icon: <Icon type="far" name="book" />,
  },
  {
    id: "create",
    value: "Crear",
    name: "Crear Expediente",
    description: "Crea un nuevo expediente digital en la base de datos.",
    icon: <Icon type="far" name="plus" />,
  },
  {
    id: "update",
    value: "Actualizar",
    name: "Actualizar Expediente",
    description: "Actualiza datos de expedientes existentes.",
    icon: <Icon type="far" name="edit" />,
  },
  {
    id: "deactivate",
    value: "Desactivar",
    name: "Desactivar Expediente",
    description: "Desactiva expedientes seleccionados.",
    icon: <Icon type="far" name="trash" />,
  },
  {
    id: "link_users",
    value: "Asociar",
    name: "Asociar registro",
    description: "Asocia registros a usuarios y otros registros.",
    icon: <Icon type="far" name="user-plus" />,
  },
];

interface ActionSelectorProps {
  selected?: ActionItem;
  onActionSelection: (actionId: ActionItem) => void;
  readonly?: boolean;
  disabled?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const ExpedienteActionSelector = ({
  onActionSelection,
  selected,
  readonly,
  disabled,
  open,
  onOpenChange,
}: ActionSelectorProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = open !== undefined ? open : internalOpen;
  const handleOpenChange = onOpenChange || setInternalOpen;

  return (
    <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          disabled={disabled}
          variant="quiet"
          group="right"
          className="h-full text-body min-w-0 max-w-1/2"
        >
          <p className="truncate">{selected?.value ?? "Acción"}</p>
          <Icon
            type="far"
            name="angle-down"
            className={cn(
              "transition-transform duration-200",
              isOpen ? "rotate-180" : "",
            )}
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="bottom"
        align="end"
        className="border-neutral h-full rounded-lg w-96.75"
      >
        {actionList.map((action) => (
          <DropdownMenuItem
            key={action.id}
            className={cn(
              "text-body text-base",
              readonly && action.id !== "read"
                ? "text-on-disabled"
                : "cursor-pointer",
            )}
            onClick={(event: React.MouseEvent) => {
              if (readonly && action.id !== "read") {
                event.preventDefault();
                return;
              }
              onActionSelection(action);
              handleOpenChange(false);
            }}
          >
            <div className="flex gap-3 items-center">
              {action.icon}
              <div>
                <p className="text-md font-semibold leading-xl">
                  {action.name}
                </p>
                <p className="text-body-quiet text-sm leading-xl">
                  {action.description}
                </p>
              </div>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
